import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useState } from 'react';
import {
	Text,
	View,
	StyleSheet,
	TouchableOpacity,
	ActivityIndicator,
	ScrollView,
	TextInput,
	Platform,
} from 'react-native';

import { useSession } from '@/components/auth';
import { describeAuthError } from '@/components/auth-error-copy';
import { useVersion } from '@/hooks/use-version';
import { API_BASE_URL, joinApiUrl } from '@/services/api-service';
import { fetchWithTimeout } from '@/services/http-service';
import { queryClient } from '@/utils/query-client';

interface AuthErrorPageProps {
	/**
	 * `overlay` sits over the still-mounted app (`AuthErrorHost`), so nothing the user typed is
	 * lost and "Try again" refetches in place. `page` is for when no user ever loaded — there is
	 * nothing underneath to keep.
	 */
	variant?: 'page' | 'overlay';
}

const AuthErrorPage: React.FC<AuthErrorPageProps> = ({ variant = 'page' }) => {
	const { session, authError, setAuthError, signOut } = useSession();
	const { version, buildNumber } = useVersion();
	const [isReporting, setIsReporting] = useState(false);
	const [reportSent, setReportSent] = useState(false);
	const [reportError, setReportError] = useState<string | null>(null);
	const [additionalInfo, setAdditionalInfo] = useState('');
	const [copied, setCopied] = useState(false);
	const [showDetails, setShowDetails] = useState(false);
	const isOverlay = variant === 'overlay';
	const copy = describeAuthError(authError, session?.email);

	const handleSignOut = async () => {
		await signOut();
		router.replace('/auth');
	};

	const handleRefresh = () => {
		if (isOverlay) {
			// The page underneath is still mounted: clear the prompt and refetch what it shows.
			// A reload here would throw away whatever the user had typed.
			setAuthError(null);
			void queryClient.invalidateQueries();
			return;
		}
		if (Platform.OS === 'web' && typeof window !== 'undefined') {
			window.location.reload();
		} else {
			setAuthError(null);
			router.replace('/');
		}
	};

	const handleCopyDebugInfo = async () => {
		const debugInfo = getDebugInfo();
		try {
			if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
				await navigator.clipboard.writeText(debugInfo);
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			} else {
				// For native platforms, we'd need expo-clipboard
				// For now, just show an alert or log
				console.log('Debug info:', debugInfo);
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			}
		} catch (error) {
			console.error('Failed to copy to clipboard:', error);
		}
	};

	const getDebugInfo = () => {
		return `
Auth Error Report
=================
Email: ${session?.email || 'Unknown'}
User ID: ${session?.id || 'Unknown'}
Provider: ${session?.provider || 'Unknown'}
Has Access Token: ${!!session?.accessToken}
Has ID Token: ${!!session?.idToken}
Has Device Token: ${!!session?.deviceToken}
Error Message: ${authError || 'No error message'}
App Version: ${version}${buildNumber ? ` (build ${buildNumber})` : ''}
API URL: ${API_BASE_URL}
Platform: ${Platform.OS}
Timestamp: ${new Date().toISOString()}
`.trim();
	};

	const handleReportProblem = async () => {
		setIsReporting(true);
		setReportError(null);

		try {
			const response = await fetchWithTimeout(joinApiUrl(API_BASE_URL, 'reports/auth-error'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					email: session?.email || 'Unknown',
					userId: session?.id || null,
					provider: session?.provider || 'Unknown',
					hasAccessToken: !!session?.accessToken,
					hasIdToken: !!session?.idToken,
					hasDeviceToken: !!session?.deviceToken,
					errorMessage: authError || 'No error message',
					appVersion: version,
					buildNumber: buildNumber || null,
					apiUrl: API_BASE_URL,
					platform: Platform.OS,
					additionalInfo: additionalInfo.trim() || null,
					timestamp: new Date().toISOString(),
				}),
			});

			if (response.ok) {
				setReportSent(true);
			} else {
				const text = await response.text();
				setReportError(`Failed to send report: ${text}`);
			}
		} catch (error) {
			setReportError(`Failed to send report: ${error instanceof Error ? error.message : 'Unknown error'}`);
		} finally {
			setIsReporting(false);
		}
	};

	const details = (
		<>
			{/* Debug Information */}
			<View style={styles.debugContainer}>
					<Text style={styles.debugTitle}>Debug Information</Text>

					<View style={styles.debugRow}>
						<Text style={styles.debugLabel}>Email:</Text>
						<Text style={styles.debugValue}>{session?.email || 'Unknown'}</Text>
					</View>

					<View style={styles.debugRow}>
						<Text style={styles.debugLabel}>Provider:</Text>
						<Text style={styles.debugValue}>{session?.provider || 'Not set'}</Text>
					</View>

					<View style={styles.debugRow}>
						<Text style={styles.debugLabel}>Error:</Text>
						<Text style={[styles.debugValue, styles.errorText]} numberOfLines={3}>
							{authError || 'No error message available'}
						</Text>
					</View>

					<View style={styles.debugRow}>
						<Text style={styles.debugLabel}>Version:</Text>
						<Text style={styles.debugValue}>
							{version}{buildNumber ? ` (${buildNumber})` : ''}
						</Text>
					</View>

					<TouchableOpacity style={styles.copyButton} onPress={handleCopyDebugInfo}>
						<Ionicons name={copied ? 'checkmark' : 'copy-outline'} size={16} color="#666" />
						<Text style={styles.copyButtonText}>
							{copied ? 'Copied!' : 'Copy Debug Info'}
						</Text>
					</TouchableOpacity>
				</View>

				{/* Report Problem Section */}
				<View style={styles.reportSection}>
					<Text style={styles.reportTitle}>Report This Problem</Text>

					{reportSent ? (
						<View style={styles.successContainer}>
							<Ionicons name="checkmark-circle" size={24} color="#10b981" />
							<Text style={styles.successText}>
								Report sent successfully! We&apos;ll look into this issue.
							</Text>
						</View>
					) : (
						<>
							<Text style={styles.reportDescription}>
								If this problem persists, please let us know so we can help resolve it.
							</Text>

							<TextInput
								style={styles.textInput}
								placeholder="Any additional details? (optional)"
								placeholderTextColor="#999"
								value={additionalInfo}
								onChangeText={setAdditionalInfo}
								multiline
								numberOfLines={3}
							/>

							{reportError && (
								<Text style={styles.reportErrorText}>{reportError}</Text>
							)}

							<TouchableOpacity
								style={[styles.button, styles.reportButton, isReporting && styles.disabledButton]}
								onPress={handleReportProblem}
								disabled={isReporting}
							>
								{isReporting ? (
									<ActivityIndicator color="white" size="small" />
								) : (
									<>
										<Ionicons name="mail-outline" size={20} color="white" />
										<Text style={styles.buttonText}>Send Report</Text>
									</>
								)}
							</TouchableOpacity>
						</>
					)}
				</View>

				<Text style={styles.footerText}>
					API: {API_BASE_URL}
				</Text>
		</>
	);

	const body = (
		<View style={[styles.content, isOverlay && styles.overlayCard]}>
			<Ionicons name="lock-closed-outline" size={isOverlay ? 40 : 56} color="#93c5fd" style={styles.icon} />

			<Text style={styles.title}>{copy.title}</Text>
			<Text style={styles.subtitle}>{copy.body}</Text>
			{isOverlay ? (
				<Text style={styles.keptNote}>
					Anything on this page is still here. Signing in again reloads the app, so copy anything you
					haven&apos;t saved first.
				</Text>
			) : null}

			<View style={styles.buttonContainer}>
				<TouchableOpacity style={[styles.button, styles.primaryButton]} onPress={handleSignOut}>
					<Ionicons name="log-in-outline" size={20} color="white" />
					<Text style={styles.buttonText}>Sign in again</Text>
				</TouchableOpacity>

				<TouchableOpacity style={[styles.button, styles.secondaryButton]} onPress={handleRefresh}>
					<Ionicons name="refresh" size={20} color="#333" />
					<Text style={[styles.buttonText, styles.secondaryButtonText]}>Try again</Text>
				</TouchableOpacity>
			</View>

			<TouchableOpacity style={styles.detailsToggle} onPress={() => setShowDetails((v) => !v)}>
				<Ionicons name={showDetails ? 'chevron-up' : 'chevron-down'} size={16} color="#888" />
				<Text style={styles.detailsToggleText}>{showDetails ? 'Hide details' : 'Show details'}</Text>
			</TouchableOpacity>
			{showDetails ? details : null}
		</View>
	);

	if (isOverlay) {
		return (
			<View style={styles.overlayBackdrop} accessibilityViewIsModal>
				<ScrollView contentContainerStyle={styles.overlayScroll}>{body}</ScrollView>
			</View>
		);
	}

	return <ScrollView contentContainerStyle={styles.container}>{body}</ScrollView>;
};

const styles = StyleSheet.create({
	container: {
		flexGrow: 1,
		backgroundColor: '#1a1a2e',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 20,
	},
	content: {
		width: '100%',
		maxWidth: 500,
		alignItems: 'center',
	},
	icon: {
		marginBottom: 16,
	},
	overlayBackdrop: {
		position: 'absolute',
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		zIndex: 1000,
		backgroundColor: 'rgba(10, 10, 25, 0.72)',
	},
	overlayScroll: {
		flexGrow: 1,
		justifyContent: 'center',
		alignItems: 'center',
		padding: 20,
	},
	overlayCard: {
		backgroundColor: '#1a1a2e',
		borderRadius: 16,
		padding: 24,
	},
	keptNote: {
		fontSize: 14,
		color: '#93c5fd',
		textAlign: 'center',
		marginTop: -12,
		marginBottom: 24,
		lineHeight: 20,
	},
	detailsToggle: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 6,
		paddingVertical: 8,
		marginBottom: 12,
	},
	detailsToggleText: {
		color: '#888',
		fontSize: 14,
	},
	title: {
		fontSize: 24,
		fontWeight: 'bold',
		color: '#fff',
		marginBottom: 12,
		textAlign: 'center',
	},
	subtitle: {
		fontSize: 16,
		color: '#aaa',
		textAlign: 'center',
		marginBottom: 24,
		lineHeight: 22,
	},
	debugContainer: {
		backgroundColor: '#252542',
		borderRadius: 12,
		padding: 16,
		width: '100%',
		marginBottom: 24,
	},
	debugTitle: {
		fontSize: 14,
		fontWeight: '600',
		color: '#888',
		marginBottom: 12,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
	debugRow: {
		flexDirection: 'row',
		marginBottom: 8,
	},
	debugLabel: {
		fontSize: 14,
		color: '#888',
		width: 80,
	},
	debugValue: {
		fontSize: 14,
		color: '#fff',
		flex: 1,
	},
	errorText: {
		color: '#f87171',
	},
	copyButton: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		marginTop: 12,
		paddingVertical: 8,
		backgroundColor: '#1a1a2e',
		borderRadius: 6,
	},
	copyButtonText: {
		color: '#888',
		marginLeft: 6,
		fontSize: 14,
	},
	buttonContainer: {
		flexDirection: 'row',
		gap: 12,
		width: '100%',
		marginBottom: 24,
	},
	button: {
		flex: 1,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		paddingVertical: 14,
		paddingHorizontal: 20,
		borderRadius: 8,
		gap: 8,
	},
	primaryButton: {
		backgroundColor: '#3b82f6',
	},
	secondaryButton: {
		backgroundColor: '#e5e5e5',
	},
	buttonText: {
		fontSize: 16,
		fontWeight: '600',
		color: '#fff',
	},
	secondaryButtonText: {
		color: '#333',
	},
	reportSection: {
		backgroundColor: '#252542',
		borderRadius: 12,
		padding: 16,
		width: '100%',
		marginBottom: 16,
	},
	reportTitle: {
		fontSize: 16,
		fontWeight: '600',
		color: '#fff',
		marginBottom: 8,
	},
	reportDescription: {
		fontSize: 14,
		color: '#aaa',
		marginBottom: 12,
	},
	textInput: {
		backgroundColor: '#1a1a2e',
		borderRadius: 8,
		padding: 12,
		color: '#fff',
		fontSize: 14,
		minHeight: 80,
		textAlignVertical: 'top',
		marginBottom: 12,
	},
	reportButton: {
		backgroundColor: '#8b5cf6',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		paddingVertical: 12,
		borderRadius: 8,
		gap: 8,
	},
	disabledButton: {
		opacity: 0.6,
	},
	reportErrorText: {
		color: '#f87171',
		fontSize: 14,
		marginBottom: 12,
	},
	successContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		backgroundColor: '#1a1a2e',
		padding: 12,
		borderRadius: 8,
	},
	successText: {
		color: '#10b981',
		fontSize: 14,
		flex: 1,
	},
	footerText: {
		color: '#666',
		fontSize: 12,
		textAlign: 'center',
	},
});

export default AuthErrorPage;
